import React, { useEffect, useState, useRef } from "react";
import { uploadTrackImage } from "../lib/uploadImage";
import { parseTagsInput } from "../lib/tags";

const UpdateModal = ({ isOpen, onClose, onConfirm, data }) => {

    const [formData, setFormData] = useState({
        title: "",
        post_url: "",
        description: "",
        image_url: "",
        platforms: [],
        tagsInput: ""
    });
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (data) {
            setFormData({
                ...data,
                platforms: data.platforms || [],
                tagsInput: Array.isArray(data.tags) ? data.tags.join(", ") : ""
            });
        }
        setError(null);
        setUploading(false);
    }, [data]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === "checkbox") {
            setFormData((prevData) => ({
                ...prevData,
                platforms: checked
                    ? [...(prevData.platforms || []), name]
                    : prevData.platforms.filter((platform) => platform !== name),
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        }
    };

    const handleFilePick = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;
        const track = formData.track;
        if (!track) {
            setError("This post has no track assigned. Cannot upload.");
            return;
        }
        setError(null);
        setUploading(true);
        try {
            const url = await uploadTrackImage({ file, track });
            setFormData((prev) => ({ ...prev, image_url: url }));
        } catch (err) {
            console.error("Upload failed", err);
            setError("Upload failed: " + err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { tagsInput, ...rest } = formData;
        onConfirm({ ...rest, tags: parseTagsInput(tagsInput) });
    }

    const handleButtonClick = (platform) => {
        setFormData((prevState) => {
          const isSelected = prevState.platforms.includes(platform);
          const newPlatforms = isSelected
            ? prevState.platforms.filter((p) => p !== platform)
            : [...prevState.platforms, platform];

          return { ...prevState, platforms: newPlatforms };
        });
      };

    return (
        <div className="fixed z-50 top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6 sm:w-3/4 md:w-2/3 lg:w-1/2 xl:w-1/2 max-h-full overflow-y-auto">
                <div className="flex justify-between items-center">
                    <button className="text-3xl" onClick={onClose}>&times;</button>
                    <div className="text-2xl"><strong>Update Post</strong></div>
                    <div></div>
                </div>
                {formData.track && (
                    <div className="mt-3 text-xs uppercase tracking-wider text-gray-500">
                        Track · <span className="text-gray-800 font-semibold">{formData.track}</span>
                    </div>
                )}
                {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}
                    <form className="mt-6">
                         <div className="mb-6">
                            <label className="block font-medium text-left mb-[5px] font-sans text-left" htmlFor="title">Title</label>
                            <input
                                className="bg-[#F1F3F4] w-full rounded-md py-3 px-4"
                                required
                                type="text"
                                id="title"
                                name="title"
                                placeholder="Enter Title"
                                value={formData.title}
                                onChange={handleChange}
                            />
                            </div>
                        <div className="mb-6">
                            <label className="block font-medium text-left mb-[5px] font-sans text-left" htmlFor="post_url">Post Link</label>
                            <input
                                className="bg-[#F1F3F4] w-full rounded-md py-3 px-4"
                                required
                                type="text"
                                id="post_url"
                                name="post_url"
                                placeholder="Post Link"
                                value={formData.post_url}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block font-medium text-left mb-[5px] font-sans text-left" htmlFor="post_url">Description</label>
                            <textarea
                                required
                                className="bg-[#F1F3F4] w-full rounded-md py-3 px-4 resize-none"
                                id="description"
                                name="description"
                                placeholder="Description"
                                rows={5}
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block font-medium text-left mb-[5px] font-sans text-left" htmlFor="image_url">Image URL</label>
                            <div className="flex gap-2">
                                <input
                                    className="bg-[#F1F3F4] flex-1 rounded-md py-3 px-4"
                                    required
                                    type="text"
                                    id="image_url"
                                    name="image_url"
                                    placeholder="Image URL"
                                    value={formData.image_url}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    disabled={uploading}
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 rounded-md disabled:opacity-50"
                                >
                                    {uploading ? "Uploading..." : "Upload"}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleFilePick}
                                />
                            </div>
                            {formData.image_url && (
                                <img
                                    src={formData.image_url}
                                    alt=""
                                    className="mt-3 max-h-40 rounded-md object-contain bg-gray-50 border border-gray-100"
                                />
                            )}
                        </div>
                        <div className="mb-6">
                            <label className="block font-medium text-left mb-[5px] font-sans text-left" htmlFor="tagsInput">Tags</label>
                            <input
                                className="bg-[#F1F3F4] w-full rounded-md py-3 px-4"
                                type="text"
                                id="tagsInput"
                                name="tagsInput"
                                placeholder="Tags (comma-separated, optional)"
                                value={formData.tagsInput}
                                onChange={handleChange}
                            />
                            <p className="mt-1 text-xs text-gray-500 text-left">
                                Lowercase, hyphens ok. Used by /share?tag=&lt;tag&gt; to bundle posts.
                            </p>
                        </div>
                        <div className="mb-6">
                            <span className="block text-left font-bold text-xl mb-2">Share To</span>
                            <div className="flex flex-wrap gap-2">
                                {['Facebook', 'X', 'Instagram', 'Threads', 'TikTok', 'WhatsApp', 'LinkedIn'].map((platform) => (
                                <button
                                    key={platform}
                                    type="button"
                                    className={`px-4 py-2 rounded-full ${
                                    formData.platforms.includes(platform.toLowerCase()) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                    onClick={() => handleButtonClick(platform.toLowerCase())}
                                >
                                    {platform}
                                </button>
                                ))}
                            </div>
                        </div>
                        <button className="w-full bg-sky-500 text-white py-3 rounded-lg hover:bg-sky-600 mb-4 disabled:opacity-50" disabled={uploading} onClick={handleSubmit}>Update</button>
                    </form>

                <button className="w-full text-[black] bg-white border text-base px-0 py-3 rounded-lg border-solid border-[rgba(0,0,0,0.4)] hover:bg-[#f5f5f5]" onClick={onClose}>Cancel</button>
            </div>
        </div>
    )
}
export default UpdateModal;
